import {
  Component,
  getUnixDate
} from "puzzle";
import { CONTENT_DATA, CUSTOM_FIELD, DETAILS_FORM_DATA, INSTRUCTIONS_DATA, SOURCE_LANGUAGE, SUBMISSION } from "./Types";
import { FILE } from "./BatchUpload/Types";
import _ from "lodash";
import { checkAndConvertToStaticMarkup } from "../util/Util";
import { calculateDueDateByAddingDays } from "../util/CalculateDueDate";

type PROPS = {
  completedStatuses: string[]
  form: any,
  submissionMode: string
}

export type STATE = {
  initEditData: any,
  submissionId: string,
  submissionStarted: boolean,
  submissionStatus: string,
  languageDirectionData: SOURCE_LANGUAGE[],
  targetLanguageData: string[],
  templateDescription: string,
  templatesData: any[],
  uploadFlowState: any
};


class SubmissionAbstract<GENERIC_PROPS = {}, GENERIC_STATE = {}> extends Component<GENERIC_PROPS & PROPS, GENERIC_STATE & STATE> {
  staticFields: string[] = [
    'submissionName',
    'project',
    'projectManager',
    'dueDate',
    'specificDate',
    'sourceLanguage',
    'targetLanguage',
    'workflow',
    'uploadedFiles',
    'deletedFiles',
    'isFavorite',
    'batchId',
    'targetFormat',
    'description',
    'template'
  ];

  public state: STATE = {
    initEditData: {},
    submissionId: 0,
    submissionStarted: false,
    submissionStatus: '',
    languageDirectionData: [],
    targetLanguageData: [],
    templateDescription: '',
    templatesData: [],
    uploadFlowState: {}
  };

  static defaultProps = {
    submissionMode: 'CREATE'
  };

  /**
   * @inheritDoc
   */
  componentWillUnmount = () => {
    super.componentWillUnmount();
    this.props.form.resetToInitialState();
  }

  /**
   * Return translated text for changing route dialog
   */
  getDialogText = () => {
    const { submissionMode } = this.props;
    let translated = {
      text: checkAndConvertToStaticMarkup(this.translate(`${submissionMode}.text`)),
      title: this.translate(`${submissionMode}.title`)
    };
    return translated;
  }

  /**
   *  Check for every targetLanguage from 'languages' array if it's not included in 'conditionalArray' and wrap with operation in that case
   * @param conditionalArray
   * @param operation  operation to wrap with targetLanguages
   * @param returnValue final array with mapped languages
   * @param targetLanguage language being mapped
   */
  reduceLanguagesWithOperations = (conditionalArray: string[], operation: string, returnValue: any[], targetLanguage: string) => {
    if (!conditionalArray.includes(targetLanguage)) {
      returnValue.push({ operation, targetLanguage })
    }
    return returnValue
  };


  /**
   * Parse batch infos for create and edit submission
   */
  parseBatchInfos = (details: any, contentData: CONTENT_DATA | any, cloneTemplate: boolean = false, isSimpleBatch: boolean) => {
    let { initEditData } = this.state;
    const submissionMode = cloneTemplate ? 'TEMPLATE_CLONE' : this.props.submissionMode;
    contentData.deletedFiles = contentData.deletedFiles ? contentData.deletedFiles.filter((item: FILE) => item.uploaded !== 'failed') : [];

    let movedFiles = [];
    let targetFormat = contentData.targetFormat;
    let targetFormatError = false;
    let uploadedFilesError = false;
    let uploadedFiles = contentData.uploadedFiles;
    let uploadedFilesParsed = [];
    let uploadedFilesLength = uploadedFiles?.length;
    let i = 0;
    let current;
    while (i < uploadedFilesLength && !targetFormatError && !uploadedFilesError) {
      current = uploadedFiles[i];
      uploadedFilesError = current.uploaded === 'failed';
      if (!uploadedFilesError && (current.documentId || current.repositoryTicket)) {
        if (targetFormat) {
          if (current.fileFormat.targetFormat && targetFormat !== current.fileFormat.targetFormat) {
            targetFormatError = true;
          }
        } else if (current.fileFormat.targetFormat) {
          targetFormat = current.fileFormat.targetFormat;
        }

        if (current.repositoryTicket) {
          uploadedFilesParsed.push({
            repositoryTicket: current.repositoryTicket,
            fileFormatId: current.fileFormat.fileFormatId
          });
        }

      }
      i++;
    }

    let operation;
    let targetLanguages = [];
    if (submissionMode === 'EDIT' || submissionMode === 'TEMPLATE_EDIT') {
      let { batchInfos: batchInfosInit } = initEditData;
      let addedTargetLanguages;
      let deletedLanguages;
      let changedWorkflow;

      let initialBatchItem = _.find(batchInfosInit, (item: any) => {
        return (item.batchId === contentData.batchId)
      });
      if (initialBatchItem) {
        let initialLanguages = initialBatchItem.targetLanguageInfos.map((item: any) => item.targetLanguage);
        if (!isSimpleBatch) {
          let selectedLanguages = contentData.targetLanguageInfos?.map((item: any) => item.targetLanguage) || contentData.targetLanguage;
          addedTargetLanguages = selectedLanguages.reduce(this.reduceLanguagesWithOperations.bind(this, initialLanguages, 'ADD_OR_UPDATE'), []);
          deletedLanguages = initialLanguages.reduce(this.reduceLanguagesWithOperations.bind(this, selectedLanguages, 'DELETE'), []);
        } else {
          let detailsLanguages = details.targetLanguage || [];
          let initialLanguages = initialBatchItem.targetLanguageInfos.map((item: any) => item.targetLanguage);
          addedTargetLanguages = detailsLanguages.reduce(this.reduceLanguagesWithOperations.bind(this, initialLanguages, 'ADD_OR_UPDATE'), []);
          deletedLanguages = initialLanguages.reduce(this.reduceLanguagesWithOperations.bind(this, detailsLanguages, 'DELETE'), []);
        }
        addedTargetLanguages = addedTargetLanguages.map((item: any) => {
          const targetLanguageInfo = contentData.targetLanguageInfos?.find((language: any) => language.targetLanguage === item.targetLanguage);
          const dueDateInfos = targetLanguageInfo.dueDateInfos = [];
          return {
            ...item,
            dueDate: contentData.dueDate,
            dueDateInfos,
            tmProfileId: targetLanguageInfo.tmProfileId,
            ...targetLanguageInfo.tmOperation && { tmOperation: targetLanguageInfo.tmOperation }
          }
        });
        targetLanguages = addedTargetLanguages.concat(deletedLanguages);

        const workflowID = contentData.workflow || contentData.workflowId;
        changedWorkflow = initialBatchItem.workflowId !== workflowID;

        let initialTargetLanguages = initialBatchItem.targetLanguageInfos;
        let initialTargetLanguagesLength = initialTargetLanguages.length;
        let current: any;
        let foundTarget;
        let contentTargetLanguages = contentData.targetLanguageInfos;
        while (initialTargetLanguagesLength) {
          initialTargetLanguagesLength--;
          current = initialTargetLanguages[initialTargetLanguagesLength];
          if (isSimpleBatch) {
            // eslint-disable-next-line no-loop-func
            foundTarget = details.targetLanguage?.find((item: any) => item === current.targetLanguage);
            if (foundTarget) {
              let contentTargetLanguage = _.find(contentData.phasesDueDate, { targetLanguage: foundTarget });
              foundTarget = {
                operation: 'ADD_OR_UPDATE',
                targetLanguage: foundTarget,
                tmProfileId: contentTargetLanguage?.tmProfileId,
                dueDate: contentData.dueDate,
                dueDateInfos: contentTargetLanguage?.dueDateInfos,
                ...contentTargetLanguage.tmOperation && { tmOperation: contentTargetLanguage.tmOperation }
              };
              targetLanguages.push(foundTarget);
            }
          } else {
            // eslint-disable-next-line no-loop-func
            foundTarget = contentTargetLanguages?.find((item: any) => item.targetLanguage === current.targetLanguage);
            if (foundTarget && (current.dueDate !== foundTarget.dueDate || current.dueDateInfos.length)) {
              foundTarget.operation = 'ADD_OR_UPDATE';
              targetLanguages.push(foundTarget);
            }
          }
        }

        //adding move files to existing batch
        movedFiles = contentData.uploadedFiles?.reduce((array: any[], item: any) => {
          const exists = !initialBatchItem.targets?.some((target: any) => item.documentId === target.documentId);
          return exists ? array.concat(item.documentId) : array;
        }, []);

      } else {
        targetLanguages = contentData.targetLanguageInfos?.map((item: any) => {
          return {
            targetLanguage: item.targetLanguage,
            operation: 'ADD_OR_UPDATE'
          }
        }) || [];

        //adding moved files to new batch
        movedFiles = contentData.uploadedFiles?.filter((item: any) => item.documentId).map((item: any) => {
          return item.documentId
        })
      }

      if (targetLanguages?.length || uploadedFilesParsed?.length || contentData.deletedFiles?.length || changedWorkflow || ((submissionMode !== 'TEMPLATE_EDIT') && movedFiles?.length) || contentData.name !== initialBatchItem?.name) {
        operation = 'ADD_OR_UPDATE';
      }
    } else {
      if (contentData.targetLanguageInfos && !isSimpleBatch) {
        targetLanguages = contentData.targetLanguageInfos.map((item: any) => {
          let language = {
            ...item,
            dueDate: cloneTemplate ? null : getUnixDate(item.dueDate),
          };

          return language;
        });
      } else {
        targetLanguages = details.targetLanguage ? details.targetLanguage.map((item: string) => {
          let phasesDueDate = contentData.phasesDueDate;
          let findLanguage = phasesDueDate?.find((phaseItem: any) => {
            return phaseItem.targetLanguage === item
          });
          return {
            targetLanguage: item,
            tmProfileId: _.find(contentData.phasesDueDate, { targetLanguage: item })?.tmProfileId,
            dueDate: cloneTemplate ? null : contentData.dueDate,
            dueDateInfos: findLanguage?.dueDateInfos || []
          }
        }) : [];
      }

    }

    let notAssignedLanguages = false;
    let langs;
    let batchInfo = details.languageDirections;
    if (contentData.targetLanguageInfos) {
      langs = contentData.targetLanguageInfos.map((item: any) => item.targetLanguage);
    } else {
      langs = details.targetLanguage;
    }

    notAssignedLanguages = langs.some((lang: string) => {
      const match = _.find(batchInfo, { targetLanguage: lang });
      return match?.notInProject;
    });

    let result = [
      {
        targetFormatError: targetFormatError,
        uploadedFilesError: uploadedFilesError,
        operation: operation,
        targetFormat: targetFormat || 'NON_PARSABLE',
        workflowId: contentData.workflow || contentData.workflowId,
        temporaryFiles: uploadedFilesParsed,
        notAssignedLanguages,
        targetLanguageInfos: targetLanguages,
        batchId: contentData.batchId,
        deletedDocumentIds: contentData.deletedFiles ? contentData.deletedFiles.filter((item: FILE) => item.documentId).map((item: FILE) => item.documentId) : [],
        movedDocumentIds: movedFiles,
        name: contentData?.name || 'Batch1' //HARDCODED DATA!!!
      }
    ];

    if (!result[0].batchId) {
      delete result[0].batchId;
    }

    if (submissionMode === 'EDIT' || submissionMode === 'TEMPLATE_EDIT') {
      if (!operation) {
        // @ts-ignore
        result = [{
          notAssignedLanguages,
          targetFormatError: targetFormatError,
          uploadedFilesError: uploadedFilesError,
        }];
      }
    } else if (result[0]) {
      delete result[0].operation;
      delete result[0].deletedDocumentIds;
      delete result[0].batchId
    }

    return result;
  };
  /**
   * Parse data for create submission request
   * @param details
   * @param contentData
   * @param instructionsData
   * @param customAttributes
   * @param cloneTemplate
   */
  parseDataForCreate = (details: DETAILS_FORM_DATA, contentData: any, instructionsData: INSTRUCTIONS_DATA, customAttributes: CUSTOM_FIELD[], cloneTemplate: boolean = false) => {
    let {
      claimScope,
      templateDueDateInDays,
      isFavorite,
      isTemplate,
      job,
      mediaAssetsNumber,
      mslaLevel,
      paClient,
      project,
      projectManager,
      requestQuote,
      sourceLanguage,
      specificDate,
      submissionName
    } = details;

    const submissionMode = cloneTemplate ? 'TEMPLATE_CLONE' : this.props.submissionMode;

    if (cloneTemplate) {
      const isNameUnchanged = this.state.initEditData.submissionName === submissionName;
      if (isNameUnchanged) {
        submissionName = this.translate('nameTemplateClone', { submissionName });
      }
      contentData = contentData.batchInfos
    }

    let failedReferenceFiles = instructionsData.referenceFiles ? instructionsData.referenceFiles.filter((item: FILE) => item.uploaded === 'failed') : [];
    let referenceFilesError = !!failedReferenceFiles.length;
    let batchInfos = contentData.length > 1 ? contentData.map((item: any) => this.parseBatchInfos(details, item, cloneTemplate, false)).flat() : this.parseBatchInfos(details, contentData, cloneTemplate, true);
    let parsedData = {
      owners: projectManager,
      dueDate: cloneTemplate || !specificDate ? null : getUnixDate(specificDate),
      projectId: project,
      instructions: !instructionsData.instructionsHtmlText || instructionsData.instructionsHtmlText === "<p></p>" ? '' : instructionsData.instructionsHtmlText,
      background: !instructionsData.notesHtmlText || instructionsData.notesHtmlText === "<p></p>" ? '' : instructionsData.notesHtmlText,
      referenceFilesError: referenceFilesError,
      referenceFiles: instructionsData.referenceFiles ? instructionsData.referenceFiles.filter((item: FILE) => item.uploaded !== 'failed').map((item: FILE) => {
        return {
          repositoryTicket: item.repositoryTicket,
          targetLanguageLocale: item.targetLanguages === 'SUBMISSION' ? null : item.targetLanguages
        }
      }) : [],
      sourceLanguage: sourceLanguage,
      batchInfos: batchInfos,
      claimScope: claimScope,
      customAttributes: customAttributes,
      name: submissionName,
      targetFormatError: batchInfos.some((item: any) => item.targetFormatError),
      uploadedFilesError: batchInfos.some((item: any) => item.uploadedFilesError),
      notAssignedLanguages: batchInfos.some((item: any) => item.notAssignedLanguages),
      isFavorite,
      isTemplate: isTemplate,
      requestQuote: requestQuote,
      isClone: submissionMode === 'CLONE' || submissionMode === 'TEMPLATE_CLONE',
      ...(mslaLevel && { mslaLevelId: mslaLevel }),
      ...(job && { paJobNumber: job }),
      ...(paClient && { paClientId: paClient }),
      mediaAssetsNumber: mediaAssetsNumber || ''
    };
    const { template: templateId } = details;

    if (isTemplate) {
      parsedData = Object.assign(parsedData, {
        templateId: templateId,
        description: details.description,
        templateDueDateInDays
      });
    } else if (templateId) {
      parsedData = Object.assign(parsedData, {
        templateId: templateId
      });
    }

    return parsedData;
  };

  /**
   * Parse and return data for edit
   */
  parseDataForEdit = (details: DETAILS_FORM_DATA, contentData: any, instructionsData: INSTRUCTIONS_DATA, customAttributes: CUSTOM_FIELD[]) => {
    const {
      claimScope,
      templateDueDateInDays,
      isFavorite,
      isTemplate,
      job,
      mediaAssetsNumber,
      mslaLevel,
      paClient,
      projectManager,
      sourceLanguage,
      specificDate,
      submissionName
    } = details;
    let {
      batchInfos: updatedBatchInfos,
      deletedBatches = []
    } = contentData;

    let targetFormatError = false;
    let uploadedFilesError = false;
    let notAssignedLanguages = false;
    let batchInfos: any = [];
    batchInfos = updatedBatchInfos?.length > 1 ? updatedBatchInfos.map((item: any) => this.parseBatchInfos(details, item, false, false)).flat() : this.parseBatchInfos(details, updatedBatchInfos, false, true);

    if (batchInfos[0]) {
      targetFormatError = batchInfos.some((item: any) => item.targetFormatError);
      uploadedFilesError = batchInfos.some((item: any) => item.uploadedFilesError);
      notAssignedLanguages = batchInfos.some((item: any) => item.notAssignedLanguages);
      batchInfos = batchInfos.filter((item: any) => item.name)
    }

    deletedBatches = deletedBatches?.map((item: any) => {
      return {
        ...item,
        operation: 'DELETE',
        targetLanguageInfos: item.targetLanguageInfos.map((languageInfo: any) => {
          return {
            targetLanguage: languageInfo.targetLanguage,
            operation: "DELETE"
          }
        })
      }
    });
    batchInfos = [...batchInfos, ...deletedBatches];

    let failedReferenceFiles = instructionsData.referenceFiles ? instructionsData.referenceFiles.filter((item: FILE) => item.uploaded === 'failed') : [];
    let referenceFilesError = !!failedReferenceFiles.length;
    let parsedData = {
      dueDate: this.props.submissionMode === 'TEMPLATE_EDIT' ? null : getUnixDate(specificDate),
      owners: projectManager,
      instructions: !instructionsData.instructionsHtmlText || instructionsData.instructionsHtmlText === "<p></p>" ? '' : instructionsData.instructionsHtmlText,
      background: !instructionsData.notesHtmlText || instructionsData.notesHtmlText === "<p></p>" ? '' : instructionsData.notesHtmlText,
      referenceFilesError: referenceFilesError,
      addedReferenceFiles: instructionsData.referenceFiles ? instructionsData.referenceFiles.filter((item: FILE) => item.uploaded !== 'failed' && item.uploaded !== 'alreadyUploaded').map((item: FILE) => {
        return {
          repositoryTicket: item.repositoryTicket,
          targetLanguageLocale: item.targetLanguages === 'SUBMISSION' ? null : item.targetLanguages
        }
      }) : [],
      deletedReferenceFiles: instructionsData.deletedReferenceFiles ? instructionsData.deletedReferenceFiles.filter((item: FILE) => item.uploaded === 'alreadyUploaded').map((item: FILE) => {
        return {
          repositoryTicket: item.repositoryTicket,
          targetLanguageLocale: item.targetLanguages === 'SUBMISSION' ? null : item.targetLanguages
        }
      }) : [],
      name: submissionName,
      sourceLanguage: sourceLanguage,
      customAttributes: customAttributes,
      batchInfos: batchInfos,
      claimScope: claimScope,
      targetFormatError: targetFormatError,
      uploadedFilesError: uploadedFilesError,
      notAssignedLanguages,
      isFavorite,
      isTemplate: details.isTemplate,
      ...(mslaLevel && { mslaLevelId: mslaLevel }),
      ...(job && { paJobNumber: job }),
      ...(paClient && { paClientId: paClient }),
      mediaAssetsNumber: mediaAssetsNumber || ''
    };

    if (isTemplate) {
      parsedData = Object.assign(parsedData, {
        description: details.description,
        templateDueDateInDays: templateDueDateInDays || 0
      });
      delete parsedData.dueDate;
    }

    return parsedData;
  };

  /**
   * Parse initial data and return it to each section
   */
  parseInitData = (data: SUBMISSION) => {
    let specificDate: any = null;
    const { submissionMode, completedStatuses } = this.props;
    if (data.dueDate !== ('rush' || 'standard')) {
      if (submissionMode !== 'CREATE') {
        specificDate = data.dueDate && new Date(data.dueDate);
      } else if (data.templateDueDateInDays) {
        specificDate = calculateDueDateByAddingDays(data.templateDueDateInDays);
      }
      data.dueDate = 'specificDate';
    }

    const mslaLevel = _.get(data, 'mslaLevel.mslaLevelId');
    const {
      projectAClientId: paClient = null,
      projectAJobNumber: job = null
    } = data;

    data.batchInfos = data.batchInfos.map((batch: any) => {
      let batchDueDate: null | number = null;
      if (submissionMode === 'CREATE' && specificDate) {
        batchDueDate = specificDate.getTime();
      }

      return {
        ...batch,
        targetLanguageInfos: batch.targetLanguageInfos.map((langItem: any) => {
          return {
            ...langItem, dueDate: langItem.dueDate ? langItem.dueDate : batchDueDate,
          }
        }),

      }
    })

    const parsedData: any = {
      details: {
        batchInfos: data.batchInfos,
        claimScope: data.claimScope,
        completed: completedStatuses ? completedStatuses.indexOf(data.status) !== -1 : false,
        description: data.description,
        dueDate: data.dueDate,
        pendingRequestQuote: data.pendingRequestQuote,
        project: data.projectId,
        projectAClientName: data.projectAClientName,
        projectManager: data.owners,
        quoteEnabled: data.quoteEnabled,
        sourceLanguage: data.sourceLanguage,
        specificDate: specificDate,
        started: data.started,
        status: data.status,
        submissionName: data.name,
        targetLanguage: data.targetLanguages,
        ...(mslaLevel && { mslaLevel }),
        ...(paClient && { paClient }),
        ...(job && { job })
      },
      content: {
        batchInfos: data.batchInfos,
        started: data.started,
        submissionId: data.submissionId
      },
      instructions: {
        instructionsHtmlText: data.instructions,
        notesHtmlText: data.background,
        referenceFiles: data.referenceFiles,
        submissionId: data.submissionId
      },
      mediaAssetsNumber: data.mediaAssetsNumber,
      customAttributes: data.customAttributes
    };
    if (submissionMode === 'TEMPLATE_EDIT') {
      parsedData.details.templateDueDateInDays = data.templateDueDateInDays;
    }

    let submissionName = data.name;
    if (submissionMode === 'CLONE') {
      data.status = '';
      parsedData.details.specificDate = null;
      parsedData.details.started = parsedData.content.started = data.started = parsedData.details.completed = false;
      if (this.state.initEditData.submissionName === undefined) {
        submissionName = this.translate('cloneSubmissionName', { submissionName });
      } else {
        submissionName = this.props.form.getData().submissionName;
      }
      parsedData.details.submissionName = submissionName;
      parsedData.content.batchInfos = data.batchInfos.map((batch: any) => {
        return {
          workflowId: batch.workflowId,
          name: batch.name,
          targetLanguageInfos: batch.targetLanguageInfos.map((langItem: any) => _.omit(langItem, ['dueDate', 'dueDateInfos']))
        }
      })
    } else if (submissionMode === 'CREATE' && data.isTemplate) {
      delete parsedData.details.submissionName;
    }

    this.setState({
      submissionId: data.submissionId,
      submissionStatus: data.status,
      submissionStarted: data.started,
      initEditData: {
        submissionName: submissionName,
        batchInfos: data.batchInfos,
        targetLanguage: data.targetLanguages,
        projectId: data.projectId,
        claimScope: data.claimScope
      }
    });

    return parsedData;
  };

  /**
   * Trigger action for submission create
   */
  triggerSubmissionAction = (action: string, data: any = {}) => {
    let { submissionMode } = this.props;
    if (submissionMode === 'EDIT' && (action !== 'selectSuppliersSubmissionAction')) {
      this.dispatch('quoteWithdrawnWarning', '', { action, submissionMode, ...data });
    } else {
      this.dispatch(action, '', { submissionMode, ...data });
    }
  };
}

export default SubmissionAbstract;
